import { FormGroup } from '@angular/forms';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { SubmissionConstants } from '../../models/constants/submissions-constants';
import { DesignChoices, DesignPointsService } from './design-points-service';

export class ZeroEnergyEligibleRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getNeemV3CustomRules(subValues: FormGroup): FormRule[] {
    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;
    const bannerText = this.getBannerText(energyEfficiencyType);
    var isZeroEnergy = this.toBoolean(subValues.get('isEBuilt')?.value);
    if (
      !isZeroEnergy &&
      (energyEfficiencyType === 'none' || !energyEfficiencyType)
    ) {
      return [
        this.hideBanner(),
        this.disableZeroEnergyEligibleControl(),
        this.clearDesignPackageErrors(),
      ];
    } else {
      return [this.setBanner(bannerText), this.clearDesignPackageErrors()];
    }
  }

  getSbraV3CustomRules(subValues: FormGroup): FormRule[] {
    const energyEfficiencyType = subValues.get('energyEfficiencyType')?.value;
    const bannerText = this.getBannerText(energyEfficiencyType);
    const status = subValues.get('status')?.value;
    const isZeroEnergyEligible = this.toBoolean(
      subValues.get('isEBuilt')?.value,
    );
    const points = this.calculatePoints(subValues);
    const sectionCount = +subValues.get('modelSectionCount')?.value;

    if (energyEfficiencyType === 'none') {
      return this.handleNoEnergyEfficiency(
        status,
        isZeroEnergyEligible,
        sectionCount,
        points,
      );
    } else if (
      energyEfficiencyType === 'originalEBuiltElectric' ||
      energyEfficiencyType === 'originalEBuiltGas' ||
      energyEfficiencyType === 'gasEBuiltPlus' ||
      energyEfficiencyType === 'electricEBuiltPlus'
    ) {
      return this.handleEnergyPlusEfficiency(
        bannerText,
        status,
        isZeroEnergyEligible,
        sectionCount,
        points,
      );
    } else {
      return this.handleNonEnergyPlusEfficiency(
        bannerText,
        status,
        points,
        sectionCount,
      );
    }
  }
  handleNonEnergyPlusEfficiency(
    bannerText: string,
    status: any,
    points: number,
    sectionCount: number,
  ): FormRule[] {
    const baseRules = [
      this.setBanner(bannerText),
      this.disableZeroEnergyEligibleControl(),
    ];
    if (status === 'Ready To Submit' && points < 10 && sectionCount > 1) {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${true}`, [
          this.createBaseEffect(RuleAction.MAKE_INVALID, 'designPackage'),
          this.createBaseEffect(
            RuleAction.SET_WARNING_TOOLTIP,
            'designPackage',
            "This submission doesn't meet the minimum points requirement for homes with multiple sections.",
          ),
        ]),
      ];
    } else {
      return [...baseRules, this.clearDesignPackageErrors()];
    }
  }
  handleEnergyPlusEfficiency(
    bannerText: string,
    status: any,
    isZeroEnergyEligible: boolean,
    sectionCount: number,
    points: number,
  ): FormRule[] {
    const baseRules = [this.setBanner(bannerText)];
    if (
      this.notEnoughPointsForZeroEnergy(status, isZeroEnergyEligible, points)
    ) {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${true}`, [
          this.createBaseEffect(RuleAction.MAKE_INVALID, 'designPackage'),
          this.createBaseEffect(
            RuleAction.SET_WARNING_TOOLTIP,
            'designPackage',
            this.submissionsConst.pointsWarning,
          ),
          this.createBaseEffect(RuleAction.SET_MIN_VALUE, 'designPackage', 18),
        ]),
      ];
    } else if (
      this.notHaveEnoughPointsForMultipleSections(
        status,
        isZeroEnergyEligible,
        sectionCount,
        points,
      )
    ) {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${true}`, [
          this.createBaseEffect(RuleAction.MAKE_INVALID, 'designPackage'),
          this.createBaseEffect(
            RuleAction.SET_WARNING_TOOLTIP,
            'designPackage',
            "This submission doesn't meet the minimum points requirement for homes with multiple sections.",
          ),
        ]),
      ];
    } else {
      return [...baseRules, this.clearDesignPackageErrors()];
    }
  }
  private notEnoughPointsForZeroEnergy(
    status: any,
    isZeroEnergyEligible: boolean,
    points: number,
  ) {
    return status === 'Ready To Submit' && isZeroEnergyEligible && points < 18;
  }

  handleNoEnergyEfficiency(
    status: any,
    isZeroEnergyEligible: boolean,
    sectionCount: number,
    points: number,
  ): FormRule[] {
    const baseRules = [
      this.hideBanner(),
      this.disableZeroEnergyEligibleControl(),
    ];
    if (
      this.notHaveEnoughPointsForMultipleSections(
        status,
        isZeroEnergyEligible,
        sectionCount,
        points,
      )
    ) {
      return [
        ...baseRules,
        this.ruleFact.createRule(`${true}`, [
          this.createBaseEffect(RuleAction.MAKE_INVALID, 'designPackage'),
          this.createBaseEffect(
            RuleAction.SET_WARNING_TOOLTIP,
            'designPackage',
            "This submission doesn't meet the minimum points requirement for homes with multiple sections.",
          ),
        ]),
      ];
    } else {
      return [...baseRules, this.clearDesignPackageErrors()];
    }
  }
  private notHaveEnoughPointsForMultipleSections(
    status: any,
    isZeroEnergyEligible: boolean,
    sectionCount: number,
    points: number,
  ) {
    return (
      status === 'Ready To Submit' &&
      !isZeroEnergyEligible &&
      sectionCount > 1 &&
      points < 10
    );
  }

  getBannerText(energyEfficiencyType: string) {
    switch (energyEfficiencyType?.toLowerCase()) {
      case 'electricebuiltplus':
        return 'This home sold as eBuilt Plus Electric';
      case 'ebuiltelectric':
        return 'This home sold as eBuilt Electric';
      case 'originalebuiltelectric':
        return 'This home sold as the "original" eBuilt Electric option and is ZERH eligible if an HVAC Heat Pump is installed';
      case 'originalebuiltgas':
        return 'This home sold as the "original" eBuilt Gas option and is ZERH eligible';
      case 'gasebuiltplus':
        return 'This home sold as eBuilt Plus Gas';
      case 'ebuiltgas':
        return 'This home sold as eBuilt Gas';
      default:
        return '';
    }
  }

  setBanner(content: string): FormRule {
    return this.ruleFact.createRule(`${true}`, [
      this.createBaseEffect(
        RuleAction.SET_VALUE,
        'energyEfficiencyTypeBanner',
        content,
      ),
      this.createBaseEffect(
        RuleAction.MAKE_VISIBLE,
        'energyEfficiencyTypeBanner',
      ),
    ]);
  }

  hideBanner(): FormRule {
    return this.ruleFact.createRule(`${true}`, [
      this.createBaseEffect(
        RuleAction.MAKE_HIDDEN,
        'energyEfficiencyTypeBanner',
      ),
    ]);
  }

  clearDesignPackageErrors(): FormRule {
    return this.ruleFact.createRule(`${true}`, [
      this.createBaseEffect(RuleAction.CLEAR_ERROR_MESSAGE, 'designPackage'),
      this.createBaseEffect(
        RuleAction.SET_WARNING_TOOLTIP,
        'designPackage',
        '',
      ),
    ]);
  }

  disableZeroEnergyEligibleControl(): FormRule {
    return this.ruleFact.createRule(`${true}`, [
      this.createBaseEffect(RuleAction.MAKE_DISABLED, 'isEBuilt'),
    ]);
  }

  calculatePoints(subValues: FormGroup): number {
    const designChoices: DesignChoices = {
      climateZone: subValues.get('climateZone')?.value,
      primarySpaceHeatingCooling: subValues.get('primarySpaceHeatingCooling')
        ?.value,
      envelope: subValues.get('envelope')?.value,
      lighting: subValues.get('lighting')?.value,
      appliances: subValues.get('appliances')?.value,
      estarClothesWasher: subValues.get('estarClothesWasher')?.value,
      bathroomWaterFixtures: subValues.get('bathroomWaterFixtures')?.value,
      waterHeating: subValues.get('waterHeating')?.value,
    };
    return DesignPointsService.calculateDesignPoints(designChoices);
  }

  createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string | boolean | number,
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }

  toBoolean(value?: string): boolean {
    if (value === undefined || value === null) {
      return false;
    } else if (typeof value === 'boolean') {
      return value;
    } else if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    } else if (typeof value === 'number') {
      return value === 1;
    }
    throw new Error(`unable to convert '${value}' to boolean`);
  }
}
